import { cn } from '@/lib/utils';
import { Calendar, ChevronDown } from 'lucide-react';
import { useState } from 'react';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

import { Button } from '../ui/button';

export interface TimelineOption {
    id: '24h' | '7d' | '30d' | 'all';
    label: string;
    hours: number | null;
}

export const TIMELINE_OPTIONS: TimelineOption[] = [
    { id: '24h', label: 'Today', hours: 24 },
    { id: '7d', label: 'Last 7 days', hours: 7 * 24 },
    { id: '30d', label: 'Last 30 days', hours: 30 * 24 },
    { id: 'all', label: 'All time', hours: null }
];

interface TimelineFilterControlProps {
    value: TimelineOption['id'];
    onChange: (id: TimelineOption['id']) => void;
}

export function TimelineFilterControl({
    value,
    onChange
}: TimelineFilterControlProps) {
    const [open, setOpen] = useState(false);
    const active =
        TIMELINE_OPTIONS.find((o) => o.id === value) ?? TIMELINE_OPTIONS[3];

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <Button
                    pill
                    className="px-3 py-4 text-sm"
                    variant="tertiary"
                    size="default"
                >
                    <Calendar />
                    {active.label}
                    <ChevronDown
                        className={cn(
                            'transition-transform',
                            open && 'rotate-180'
                        )}
                    />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-40">
                <DropdownMenuRadioGroup
                    value={value}
                    onValueChange={(id) => onChange(id as TimelineOption['id'])}
                >
                    {TIMELINE_OPTIONS.map((option) => {
                        const isActive = option.id === value;
                        return (
                            <DropdownMenuRadioItem
                                key={option.id}
                                value={option.id}
                                className="[&>span:first-child]:hidden gap-1.5 py-1.5 pl-2"
                            >
                                <span
                                    className={cn(
                                        'flex justify-center items-center border rounded-full size-4 shrink-0',
                                        isActive
                                            ? 'border-foreground'
                                            : 'border-muted-foreground'
                                    )}
                                >
                                    {isActive && (
                                        <span className="bg-foreground rounded-full size-2" />
                                    )}
                                </span>
                                {option.label}
                            </DropdownMenuRadioItem>
                        );
                    })}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
